import { useMemo, useState } from 'react';
import type { BidEntry } from '../data/mockLots';
import { useLotBidsInfinite } from '../hooks/useLotBidsInfinite';
import { placeholderBidder } from '../lib/lots';
import { BidHistoryRow } from './BidHistoryRow';

type Props = {
  lotId: string;
};

export function FullBidHistory({ lotId }: Props) {
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useLotBidsInfinite(lotId, true);

  const [pageIdx, setPageIdx] = useState(0);
  const pages = data?.pages ?? [];
  const currentPage = pages[pageIdx];

  const entries = useMemo<BidEntry[]>(
    () =>
      (currentPage?.bids ?? []).map((rb) => ({
        ts: rb.placed_at,
        bidder: placeholderBidder(rb.user_id, rb.first_name, rb.username),
        price: rb.price_after_cents / 100,
        bidIndex: rb.bid_index,
        extensionMs: rb.timer_extension_ms ?? undefined,
        userBidSeq: rb.user_bid_seq ?? undefined,
      })),
    [currentPage],
  );

  const canPrev = pageIdx > 0;
  const canNext = pageIdx < pages.length - 1 || hasNextPage;
  const firstIndex = entries[0]?.bidIndex;
  const lastIndex = entries[entries.length - 1]?.bidIndex;
  const indexWidth = firstIndex != null ? String(firstIndex).length : 2;

  const goNext = () => {
    if (pageIdx < pages.length - 1) {
      setPageIdx(pageIdx + 1);
      return;
    }
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage().then(() => setPageIdx((i) => i + 1));
    }
  };

  const goPrev = () => {
    if (canPrev) setPageIdx(pageIdx - 1);
  };

  // Reserve a fixed 10-row slot (360px) on full pages + during load so paging
  // between full pages never reflows surrounding content. The terminal page may
  // be shorter — there we drop the reservation, accepting a one-time shrink
  // when the user arrives at the end (closed lot only, no concurrent updates).
  const PAGE_SIZE = 10;
  const isFullSlot = isLoading || entries.length === PAGE_SIZE;
  return (
    <section>
      <div className={isFullSlot ? 'min-h-[360px]' : undefined}>
        {isLoading && (
          <p className="px-3 py-4 text-center font-mono text-[10px] tracking-[0.2em] text-fg-muted">
            LOADING…
          </p>
        )}
        {isError && (
          <p className="px-3 py-4 text-center font-mono text-[10px] tracking-[0.2em] text-danger">
            FAILED TO LOAD HISTORY
          </p>
        )}
        {!isLoading && !isError && entries.length > 0 && (
          <div className="flex flex-col">
            {entries.map((entry) => (
              <BidHistoryRow
                key={`hist-${entry.bidIndex}`}
                entry={entry}
                isLatest={false}
                count={entry.userBidSeq}
                indexWidth={indexWidth}
              />
            ))}
          </div>
        )}
      </div>

      {!isLoading && !isError && entries.length > 0 && (
        <div className="px-3 mt-2 pt-3 border-t border-edge flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={goPrev}
            disabled={!canPrev || isFetchingNextPage}
            className="h-10 w-20 font-mono text-[11px] tracking-[0.2em] text-primary disabled:text-fg-dim disabled:cursor-default hover:text-fg transition-colors flex items-center justify-center gap-1.5"
            aria-label="newer page"
          >
            <span>←</span>
            <span>prev</span>
          </button>
          <span className="font-mono text-[10px] tracking-[0.18em] text-fg-muted tabular-nums">
            {firstIndex != null && lastIndex != null ? `#${firstIndex} — #${lastIndex}` : '—'}
          </span>
          <button
            type="button"
            onClick={goNext}
            disabled={!canNext || isFetchingNextPage}
            className="h-10 w-20 font-mono text-[11px] tracking-[0.2em] text-primary disabled:text-fg-dim disabled:cursor-default hover:text-fg transition-colors flex items-center justify-center gap-1.5"
            aria-label="older page"
          >
            <span>{isFetchingNextPage ? '…' : 'next'}</span>
            <span>→</span>
          </button>
        </div>
      )}
    </section>
  );
}
